import React, { useEffect, useMemo, useRef, useState } from 'react';

export const MessageEditor: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}> = ({ value, onChange, placeholder, rows = 4 }) => {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  // Na mobile chowamy pasek formatowania (B/I/nagłówki/listy) — sam plain text.
  // Markdown nadal można wpisać ręcznie, po prostu bez przycisków.
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767.98px)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767.98px)');
    const onChangeMq = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', onChangeMq);
    return () => mq.removeEventListener('change', onChangeMq);
  }, []);

  const wrap = (before: string, after: string = before) => {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    const sel = value.slice(start, end);
    const next = value.slice(0, start) + before + (sel || 'tekst') + after + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      const newStart = start + before.length;
      const newEnd = newStart + (sel || 'tekst').length;
      ta.setSelectionRange(newStart, newEnd);
    });
  };

  const prefixLine = (prefix: string) => {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart ?? 0;
    const lineStart = value.lastIndexOf('\n', start - 1) + 1;
    const isEmptyLine = lineStart === start || value[lineStart] === '\n';
    const insert = isEmptyLine ? prefix : '\n' + prefix;
    const pos = isEmptyLine ? lineStart : start;
    const next = value.slice(0, pos) + insert + value.slice(pos);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      const caret = pos + insert.length;
      ta.setSelectionRange(caret, caret);
    });
  };

  const btnStyle: React.CSSProperties = {
    padding: '0.3rem 0.55rem',
    fontSize: '0.8125rem',
    fontWeight: 600,
    border: '1px solid var(--color-border)',
    background: 'var(--color-surface)',
    color: 'var(--color-text-secondary)',
    borderRadius: '4px',
    cursor: 'pointer',
    minWidth: 30,
    lineHeight: 1,
  };

  return (
    <div style={{ border: '1px solid var(--color-border-strong)', borderRadius: 'var(--radius)', overflow: 'hidden', background: 'var(--color-surface)' }}>
      {!isMobile && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.25rem',
          padding: '0.4rem 0.5rem',
          background: 'var(--color-surface-alt)',
          borderBottom: '1px solid var(--color-border)',
        }}>
          <button type="button" style={{ ...btnStyle, fontWeight: 800 }} title="Pogrubienie (**tekst**)"
            onClick={() => wrap('**')}>B</button>
          <button type="button" style={{ ...btnStyle, fontStyle: 'italic' }} title="Kursywa (*tekst*)"
            onClick={() => wrap('*')}>I</button>
          <button type="button" style={btnStyle} title="Kod inline (`tekst`)"
            onClick={() => wrap('`')}>{'</>'}</button>
          <span style={{ width: 1, background: 'var(--color-border)', margin: '0 0.25rem' }} />
          <button type="button" style={{ ...btnStyle, fontSize: '0.875rem', fontWeight: 700 }} title="Nagłówek duży"
            onClick={() => prefixLine('# ')}>H1</button>
          <button type="button" style={{ ...btnStyle, fontSize: '0.8125rem', fontWeight: 700 }} title="Nagłówek średni"
            onClick={() => prefixLine('## ')}>H2</button>
          <button type="button" style={{ ...btnStyle, fontSize: '0.75rem', fontWeight: 700 }} title="Nagłówek mały"
            onClick={() => prefixLine('### ')}>H3</button>
          <span style={{ width: 1, background: 'var(--color-border)', margin: '0 0.25rem' }} />
          <button type="button" style={btnStyle} title="Lista punktowana"
            onClick={() => prefixLine('- ')}>• Lista</button>
          <button type="button" style={btnStyle} title="Lista numerowana"
            onClick={() => prefixLine('1. ')}>1. Lista</button>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', alignSelf: 'center', padding: '0 0.25rem' }}>
            Markdown: **bold** *italic* `code` # nagłówek
          </span>
        </div>
      )}
      <textarea
        ref={ref}
        className="form-input"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        style={{
          border: 'none',
          borderRadius: 0,
          resize: 'vertical',
          width: '100%',
          fontFamily: 'inherit',
          minHeight: `${rows * 1.5}rem`,
        }}
      />
    </div>
  );
};

/**
 * Zmemoizowany renderer markdown. Parsowanie (renderMarkdownContent) jest czyste,
 * więc wynik zależy wyłącznie od content+kolorów — cache'ujemy go per te wartości i
 * pomijamy re-render, gdy się nie zmieniły. Propsy to stringi, więc React.memo porównuje
 * je po wartości (brak churnu referencji z pollingu). Zachowanie identyczne z bezpośrednim
 * wywołaniem renderMarkdownContent — tylko bez zbędnego re-parsowania na każdym renderze.
 */
export const Markdown: React.FC<{ content: string; color?: string; borderColor?: string }> = React.memo(
  function Markdown({ content, color = 'var(--color-primary)', borderColor = 'var(--color-primary)' }) {
    const nodes = useMemo(
      () => renderMarkdownContent(content, color, borderColor),
      [content, color, borderColor]
    );
    return <>{nodes}</>;
  }
);

export function renderInline(text: string, color = 'var(--color-primary)', borderColor = 'var(--color-primary)'): React.ReactNode[] {
  const tokens: { type: 'text' | 'ref' | 'bold' | 'italic' | 'code'; value: string }[] = [];
  const re = /(\[(\d+)\])|(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(`([^`]+)`)/g;
  let lastIdx = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIdx) tokens.push({ type: 'text', value: text.slice(lastIdx, m.index) });
    if (m[2]) tokens.push({ type: 'ref', value: m[2] });
    else if (m[4]) tokens.push({ type: 'bold', value: m[4] });
    else if (m[6]) tokens.push({ type: 'italic', value: m[6] });
    else if (m[8]) tokens.push({ type: 'code', value: m[8] });
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) tokens.push({ type: 'text', value: text.slice(lastIdx) });

  return tokens.map((t, i) => {
    if (t.type === 'text') return <React.Fragment key={i}>{t.value}</React.Fragment>;
    if (t.type === 'bold') return <strong key={i} style={{ fontWeight: 700, color: 'var(--color-text)' }}>{t.value}</strong>;
    if (t.type === 'italic') return <em key={i}>{t.value}</em>;
    if (t.type === 'code') return (
      <code key={i} style={{
        fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
        fontSize: '0.875em',
        background: 'rgba(0,0,0,0.06)',
        padding: '0 0.3rem',
        borderRadius: 3,
      }}>{t.value}</code>
    );
    return (
      <span key={i} title={`Odnośnik [${t.value}] — patrz sekcja Źródła`} style={{
        display: 'inline-block',
        padding: '0 0.4rem',
        margin: '0 0.1rem',
        fontSize: '0.72rem',
        fontWeight: 700,
        color,
        background: '#ffffff',
        border: `1.5px solid ${borderColor}`,
        borderRadius: 6,
        fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
        lineHeight: 1.6,
        verticalAlign: 'baseline',
        letterSpacing: '-0.02em',
        boxShadow: `0 1px 0 ${borderColor}40`,
      }}>[{t.value}]</span>
    );
  });
}

export function renderMarkdownContent(content: string, color = 'var(--color-primary)', borderColor = 'var(--color-primary)'): React.ReactNode[] {
  if (!content) return [];
  const lines = content.split('\n');
  const blocks: React.ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    const h = /^(#{1,3})\s+(.+)$/.exec(line);
    if (h) {
      const level = h[1].length;
      const text = h[2];
      const baseStyle: React.CSSProperties = { margin: '0.5rem 0 0.25rem', fontWeight: 700, color: 'var(--color-text)' };
      if (level === 1) blocks.push(<h3 key={key++} style={{ ...baseStyle, fontSize: '1.125rem' }}>{renderInline(text, color, borderColor)}</h3>);
      else if (level === 2) blocks.push(<h4 key={key++} style={{ ...baseStyle, fontSize: '1rem' }}>{renderInline(text, color, borderColor)}</h4>);
      else blocks.push(<h5 key={key++} style={{ ...baseStyle, fontSize: '0.9375rem' }}>{renderInline(text, color, borderColor)}</h5>);
      i++;
      continue;
    }

    if (/^-\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^-\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^-\s+/, ''));
        i++;
      }
      blocks.push(
        <ul key={key++} style={{ margin: '0.25rem 0 0.5rem', paddingLeft: '1.25rem' }}>
          {items.map((it, idx) => <li key={idx}>{renderInline(it, color, borderColor)}</li>)}
        </ul>
      );
      continue;
    }

    if (/^\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s+/, ''));
        i++;
      }
      blocks.push(
        <ol key={key++} style={{ margin: '0.25rem 0 0.5rem', paddingLeft: '1.5rem' }}>
          {items.map((it, idx) => <li key={idx}>{renderInline(it, color, borderColor)}</li>)}
        </ol>
      );
      continue;
    }

    if (line.trim() === '') {
      i++;
      continue;
    }

    const paraLines: string[] = [];
    while (i < lines.length && lines[i].trim() !== '' && !/^(#{1,3}\s|-\s|\d+\.\s)/.test(lines[i])) {
      paraLines.push(lines[i]);
      i++;
    }
    const joined = paraLines.join('\n');
    blocks.push(
      <p key={key++} style={{ margin: '0 0 0.5rem', whiteSpace: 'pre-wrap' }}>
        {renderInline(joined, color, borderColor)}
      </p>
    );
  }

  return blocks;
}
